import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GeneralExpenseService } from "../../../../services/generalExpense/generalExpenseService";
import type { DateRange } from "../utils/date";
import { ProviderStatCard } from "../../components/providers/ProviderStatCard";
import { FaMoneyBillWave, FaChartBar, FaCalendarAlt, FaFileDownload, FaChartLine } from "react-icons/fa";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: (i = 1) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }),
};

interface Props {
    range: DateRange;
    refreshKey: number;
}

export const GeneralExpenseStatsTab: React.FC<Props> = ({ range, refreshKey }) => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const service = new GeneralExpenseService();
            const res = await service.getStats({ dateFrom: range.start, dateTo: range.end });
            const body = res.getResponseData();
            if (body?.success) {
                setStats(body.data);
            }
        } catch (e: any) {
            setError(e?.message || "Error obteniendo estadísticas");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, [range.start, range.end, refreshKey]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const chartData = stats?.monthlyData?.map((item: any) => ({
        name: item.month,
        amount: item.amount,
        generalAmount: item.generalAmount ?? 0,
        stockAmount: item.stockAmount ?? 0,
        salaryAmount: item.salaryAmount ?? 0,
    })) || [];

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) return null;
        const p = payload[0]?.payload;
        if (!p) return null;

        return (
            <div className="rounded-xl border bg-white px-4 py-3 shadow-sm">
                <div className="text-sm font-semibold text-gray-900">{label}</div>
                <div className="mt-2 space-y-1 text-sm">
                    <div className="flex justify-between gap-6">
                        <span className="text-gray-600">Total</span>
                        <span className="font-medium text-gray-900">{formatCurrency(Number(p.amount || 0))}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                        <span className="text-gray-600">Generales</span>
                        <span className="font-medium text-gray-900">{formatCurrency(Number(p.generalAmount || 0))}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                        <span className="text-gray-600">Stock</span>
                        <span className="font-medium text-gray-900">{formatCurrency(Number(p.stockAmount || 0))}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                        <span className="text-gray-600">Salarios</span>
                        <span className="font-medium text-gray-900">{formatCurrency(Number(p.salaryAmount || 0))}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header con Exportar */}
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Resumen de Gastos</h3>
            </div>
            {/* Fila de Tarjetas de Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
                    <ProviderStatCard
                        title="Total Gastos"
                        value={formatCurrency(stats?.totalAmount || 0)}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-indigo-500"
                        icon={<FaMoneyBillWave className="h-5 w-5 text-indigo-500" />}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
                    <ProviderStatCard
                        title="Nº Gastos"
                        value={stats?.count || 0}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-emerald-500"
                        icon={<FaCalendarAlt className="h-5 w-5 text-emerald-500" />}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={2}>
                    <ProviderStatCard
                        title="Media Gasto"
                        value={formatCurrency(stats?.averageAmount || 0)}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-amber-500"
                        icon={<FaChartLine className="h-5 w-5 text-amber-500" />}
                    />
                </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
                    <ProviderStatCard
                        title="Gastos generales"
                        value={formatCurrency(stats?.totalGeneralAmount || 0)}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-slate-500"
                        icon={<FaMoneyBillWave className="h-5 w-5 text-slate-500" />}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
                    <ProviderStatCard
                        title="Coste stock"
                        value={formatCurrency(stats?.totalStockAmount || 0)}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-fuchsia-500"
                        icon={<FaMoneyBillWave className="h-5 w-5 text-fuchsia-500" />}
                    />
                </motion.div>
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={5}>
                    <ProviderStatCard
                        title="Coste salarios"
                        value={formatCurrency(stats?.totalSalaryAmount || 0)}
                        loading={loading}
                        error={error}
                        className="border-l-4 border-l-cyan-500"
                        icon={<FaMoneyBillWave className="h-5 w-5 text-cyan-500" />}
                    />
                </motion.div>
            </div>

            {/* Gráfico de Gastos Mensuales */}
            <div className="grid grid-cols-1 gap-6">
                <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
                    <div className="rounded-2xl border bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-base font-semibold text-gray-800">Evolución de Gastos</h3>
                                <p className="text-sm text-gray-500">Gasto total por mes</p>
                            </div>
                            <FaChartBar className="h-5 w-5 text-gray-400" />
                        </div>
                        
                        <div className="h-80 w-full">
                            {loading ? (
                                <div className="flex h-full items-center justify-center">
                                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
                                </div>
                            ) : error ? (
                                <div className="flex h-full items-center justify-center text-rose-600">
                                    {error}
                                </div>
                            ) : chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                        <XAxis 
                                            dataKey="name" 
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#6b7280', fontSize: 12 }}
                                            dy={10}
                                        />
                                        <YAxis 
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#6b7280', fontSize: 12 }}
                                            tickFormatter={(value) => `${value}€`}
                                        />
                                        <Tooltip cursor={{ fill: '#f3f4f6' }} content={<CustomTooltip />} />
                                        <Bar 
                                            dataKey="amount" 
                                            fill="#6366f1" 
                                            radius={[6, 6, 0, 0]}
                                            barSize={40}
                                        >
                                            {chartData.map((entry: any, index: number) => (
                                                <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#6366f1' : '#818cf8'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex h-full items-center justify-center text-gray-400">
                                    No hay datos disponibles para mostrar el gráfico
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};
